import { defineStore } from 'pinia'
import { conformIcon } from '../lib/color'
import { applyFonts } from '../lib/fonts'
import { fitsPalette, formatPixels, parsePixels } from '../lib/pixels'
import { can } from '../lib/roles'
import { computed, markRaw, ref, shallowRef, watch } from 'vue'
import { Api, describeError } from '../api/http'
import type { ConversationInfo, Features, LibraryKind, LibraryRef, PlayResponse, Role, SpaceInfo, SyncResponse, UserProfile, UserId, Uuid } from '../api/types'
import { NotificationSocket, type SocketState } from '../api/ws'
import { loadCore, newConversationId } from '../core/wasm'
import { mergeMessages } from '../lib/format'
import { keepLocally, localAttachment } from '../lib/attachmentStore'
import { transferFromLink, type LinkTransfer } from '../lib/metacrypto'
import { notTransferred, receiveHistory, stopTransfer, type TransferSource } from '../lib/transfer'
import { IdbStorage, type StoredMessage } from '../storage/db'
import { Engine, allConversations } from '../sync/engine'
import { usePulls } from './pulls'
import { useRing } from './ring'
import { useUnread } from './unread'
import { appInvoke, isApp, serverBase } from '../platform'

export type Status = 'loading' | 'signed-out' | 'ready' | 'error' | 'other-tab'

export interface Toast {
  id: number
  kind: 'info' | 'error'
  text: string
}

/** App-wide session: the engine, synced server state, and decrypted messages. */
export const useSession = defineStore('session', () => {
  const status = ref<Status>('loading')
  const bootError = ref<string | null>(null)
  const engine = shallowRef<Engine | null>(null)
  const sync = shallowRef<SyncResponse | null>(null)
  const socketState = ref<SocketState>('closed')
  const messages = ref<Record<string, StoredMessage[]>>({})
  const toasts = ref<Toast[]>([])
  const isAdmin = ref(false)
  /** The server lets me create a space right now (policy, approval and cap). */
  const canCreateSpaces = ref(false)
  /** I may attach storage of my own and create libraries in it (admins always may). */
  const canLinkStorage = ref(false)
  /** Some server storage is open to me for libraries of my own (members). */
  /** Users currently online (from sync, kept fresh by presence notifications). */
  const online = ref<Set<string>>(new Set())
  /** Nodes that connected or went away since their status was fetched, by location. */
  const nodeOnline = ref<Record<string, boolean>>({})
  /** Who is in each conversation's call (from the server; updated by `call` frames). */
  const inCall = ref<Record<Uuid, string[]>>({})
  /** Who's in each conversation's watch party / listening room (server presence). */
  const inParty = ref<Record<Uuid, string[]>>({})
  const features = ref<Features>({ calls: false })
  const serverName = ref('')
  /** Colours this server offers for designed pictures and space symbols. */
  const palette = ref<string[]>([])
  /** The server allows profile photos (else designed pictures only). */
  const profilePhotos = ref(true)
  let socket: NotificationSocket | null = null
  let catchUp: ReturnType<typeof setInterval> | null = null
  let storage: IdbStorage | null = null
  let toastId = 0
  const api = markRaw(new Api(serverBase()))

  const me = computed(() => engine.value?.userId ?? null)
  const spaces = computed<SpaceInfo[]>(() => sync.value?.spaces ?? [])
  const dms = computed<ConversationInfo[]>(() => sync.value?.conversations ?? [])
  /** Conversations I muted: no push notifications, and no notifications on this device. */
  const muted = computed(() => new Set(sync.value?.muted ?? []))
  const profiles = computed(() => {
    const m = new Map<string, UserProfile>()
    for (const p of sync.value?.profiles ?? []) m.set(p.user_id, p)
    return m
  })
  const conversations = computed(() => {
    const m = new Map<string, ConversationInfo>()
    if (sync.value) for (const c of allConversations(sync.value)) m.set(c.id, c)
    return m
  })

  function toast(text: string, kind: Toast['kind'] = 'info'): void {
    if (kind === 'error') console.warn(`[hrth] ${text}`)
    const id = ++toastId
    toasts.value.push({ id, kind, text })
    setTimeout(() => (toasts.value = toasts.value.filter((t) => t.id !== id)), kind === 'error' ? 8000 : 4000)
  }

  function attach(e: Engine): void {
    engine.value = markRaw(e)
    void storage?.getKv<string[]>('notTransferred').then((ids) => ids?.forEach((id) => notTransferred.add(id)))
    e.on((ev) => {
      if (ev.type === 'sync') {
        sync.value = ev.sync
        online.value = new Set(ev.sync.online ?? [])
      }
      if (ev.type === 'messages') {
        messages.value[ev.conv] = mergeMessages(messages.value[ev.conv] ?? [], ev.messages)
        useUnread().onMessages(ev.conv, ev.messages)
      }
      if (ev.type === 'warning') toast(ev.message, 'error')
    })
    socket?.stop()
    socket = new NotificationSocket({
      baseUrl: serverBase(),
      token: () => e.token,
      onState: (s) => (socketState.value = s),
      onReady: () => void refresh(),
      onFrame: (f) => {
        if (f.t === 'call') {
          inCall.value = { ...inCall.value, [f.conversation_id]: f.participants }
          return
        }
        if (f.t === 'ring') return useRing().onRing(f.conversation_id, f.from)
        if (f.t === 'ring_ended') return useRing().onRingEnded(f.conversation_id, f.reason)
        if (f.t === 'ring_declined') return useRing().onDeclined(f.conversation_id, f.by)
        if (f.t === 'party') {
          inParty.value = { ...inParty.value, [f.conversation_id]: f.participants }
          return
        }
        if (f.t === 'pull') {
          void usePulls().onFrame(f.library, f.pull)
          return
        }
        if (f.t === 'node') {
          nodeOnline.value = { ...nodeOnline.value, [f.location]: f.online }
          return
        }
        if (f.t === 'presence') {
          const next = new Set(online.value)
          if (f.online) next.add(f.user_id)
          else next.delete(f.user_id)
          online.value = next
          return
        }
        e.handleFrame(f).catch((err) => toast(describeError(err), 'error'))
      },
    })
    socket.start()
    // Pokes are hints; if one is lost (flaky network, sleeping laptop) this catch-up
    // makes sure nothing stays missing for long.
    if (catchUp) clearInterval(catchUp)
    catchUp = setInterval(() => {
      if (!document.hidden && engine.value === e) {
        void refresh()
        void usePulls().refresh()
      }
    }, 30_000)
    status.value = 'ready'
    // Push needs both: whether it's on here and the server's key.
    void Promise.all([useUnread().load(), loadInstance()]).then(() => useUnread().syncPush())
    void usePulls().refresh()
    void loadMe()
  }

  async function openStorage(): Promise<IdbStorage> {
    storage ??= await IdbStorage.open()
    return storage
  }

  // Only one tab may drive this device's encryption state at a time: two tabs would
  // each advance their own copy of the MLS ratchets and overwrite each other's saves.
  let releaseTabLock: (() => void) | null = null

  function acquireTabLock(steal: boolean): Promise<boolean> {
    const locks = globalThis.navigator?.locks
    if (!locks) return Promise.resolve(true)
    return new Promise((resolve) => {
      locks
        .request('hearth-device', steal ? { steal: true } : { ifAvailable: true }, (lock) => {
          if (!lock) {
            resolve(false)
            return undefined
          }
          resolve(true)
          return new Promise<void>((release) => (releaseTabLock = release))
        })
        .catch(() => onLockStolen())
    })
  }

  function onLockStolen(): void {
    if (catchUp) clearInterval(catchUp)
    socket?.stop()
    socket = null
    engine.value = null
    releaseTabLock = null
    status.value = 'other-tab'
  }

  /** Takes over from another tab, then boots normally. */
  async function useHere(): Promise<void> {
    status.value = 'loading'
    await acquireTabLock(true)
    await bootLocked()
  }

  /** Loads WASM and resumes a saved session if there is one. */
  async function boot(): Promise<void> {
    if (!releaseTabLock && !(await acquireTabLock(false))) {
      status.value = 'other-tab'
      return
    }
    await bootLocked()
  }

  async function bootLocked(): Promise<void> {
    try {
      await loadCore()
      // Ask the browser not to evict our keys under storage pressure.
      void navigator.storage?.persist?.()
      const e = await Engine.resume(api, await openStorage())
      if (e) {
        attach(e)
        await refresh()
      } else {
        status.value = 'signed-out'
      }
    } catch (err) {
      bootError.value = describeError(err)
      status.value = 'error'
    }
  }

  let preloaded = false

  async function refresh(): Promise<void> {
    if (!engine.value) return
    try {
      const s = await engine.value.reconcile()
      // Load recent history for every conversation once, so unread counts include
      // messages that arrived before this page load.
      if (!preloaded) {
        preloaded = true
        await Promise.all(allConversations(s).map((c) => loadMessages(c.id)))
      }
    } catch (err) {
      toast(describeError(err), 'error')
    }
  }

  /** Server export + this device's decrypted history, as one JSON document. */
  async function exportData(): Promise<Blob> {
    const server = await requireEngine().api.exportAccount()
    const history = (await storage?.allMessages()) ?? []
    const doc = {
      format: 'hearth-export/v1',
      account: server,
      history: { device: requireEngine().deviceId, messages: history },
    }
    return new Blob([JSON.stringify(doc, null, 1)], { type: 'application/json' })
  }

  /** Imports message history from an export file for conversations this account is in. */
  async function importHistory(file: File): Promise<number> {
    const doc = JSON.parse(await file.text()) as { format?: string; history?: { messages?: StoredMessage[] } }
    if (doc.format !== 'hearth-export/v1' || !Array.isArray(doc.history?.messages)) {
      throw new Error('That isn’t a hrth export file.')
    }
    return importMessages(doc.history!.messages!)
  }

  /** Adds messages of conversations this account is in; ones already here are kept. */
  async function importMessages(all: StoredMessage[]): Promise<number> {
    const mine = new Set(conversations.value.keys())
    const msgs = all.filter((m) => mine.has(m.conv))
    const added = (await storage?.importMessages(msgs)) ?? 0
    await Promise.all([...new Set(msgs.map((m) => m.conv))].map((c) => loadMessages(c)))
    return added
  }

  /** What this device would send in a history transfer (HRTH-202). */
  function transferSource(): TransferSource {
    return { messages: async () => (await storage?.allMessages()) ?? [], attachment: localAttachment }
  }

  /** The new device's side of a transfer its link offered; runs once it has joined its conversations. */
  function receiveTransfer(link: LinkTransfer): void {
    void receiveHistory(api, link, {
      importMessages,
      keepAttachment: keepLocally,
      async notTransferred(ids) {
        for (const id of ids) notTransferred.add(id)
        await storage?.setKv('notTransferred', [...notTransferred])
      },
    })
  }

  /** Where to land after joining a space: straight into its first channel. */
  function spaceLanding(space: Uuid | null) {
    if (!space) return { name: 'home' as const }
    const first = spaces.value.find((s) => s.id === space)?.channels[0]
    return first
      ? { name: 'space' as const, params: { spaceId: space, convId: first.id } }
      : { name: 'space' as const, params: { spaceId: space } }
  }

  function storageHandle(): IdbStorage | null {
    return storage
  }

  async function register(invite: string, username: string, displayName: string): Promise<Uuid | null> {
    await loadCore()
    const { engine: e, spaceId } = await Engine.register(api, await openStorage(), invite, username, displayName)
    attach(e)
    await refresh()
    return spaceId
  }

  async function linkDevice(link: string): Promise<void> {
    await loadCore()
    attach(await Engine.link(api, await openStorage(), link))
    await refresh()
    const transfer = transferFromLink(link)
    if (transfer) receiveTransfer(transfer)
  }

  async function recoverAccount(userId: string, code: string): Promise<void> {
    await loadCore()
    attach(await Engine.recover(api, await openStorage(), userId, code))
    await refresh()
  }

  async function acceptInvite(invite: string): Promise<Uuid | null> {
    const e = requireEngine()
    const space = await e.acceptInvite(invite)
    await refresh()
    return space
  }

  function requireEngine(): Engine {
    if (!engine.value) throw new Error('not signed in')
    return engine.value
  }

  async function loadMessages(conv: Uuid): Promise<void> {
    const e = engine.value
    if (!e) return
    const stored = await e.messages(conv)
    messages.value[conv] = mergeMessages(messages.value[conv] ?? [], stored)
  }

  async function send(conv: Uuid, text: string, replyTo?: Uuid, mentions?: UserId[]): Promise<void> {
    await requireEngine().sendText(conv, text, replyTo, mentions)
  }

  function envelopeBase() {
    return { v: 1, id: newConversationId(), ts: Date.now() }
  }

  async function react(conv: Uuid, target: Uuid, emoji: string, remove = false): Promise<void> {
    await requireEngine().sendEnvelope(conv, { ...envelopeBase(), type: 'reaction', target, emoji, ...(remove ? { remove } : {}) })
  }

  async function editMessage(conv: Uuid, target: Uuid, body: string): Promise<void> {
    await requireEngine().sendEnvelope(conv, { ...envelopeBase(), type: 'edit', target, body })
  }

  async function deleteMessage(conv: Uuid, target: Uuid): Promise<void> {
    await requireEngine().sendEnvelope(conv, { ...envelopeBase(), type: 'delete', target })
  }

  /**
   * Delete from device (HRTH-134): the message, its reactions, edits and deletes, and any
   * attachment kept for it leave this device only. Nobody else, and no server, is told.
   */
  async function deleteFromDevice(conv: Uuid, id: Uuid): Promise<void> {
    const all = messages.value[conv] ?? []
    const gone = all.filter((m) => m.envelope.id === id || ('target' in m.envelope && m.envelope.target === id))
    await requireEngine().deleteLocal(conv, gone.map((m) => m.seq))
    for (const m of gone) {
      if (m.envelope.type === 'media') await import('../lib/attachmentStore').then((s) => s.forgetAttachment((m.envelope as { media: { id: string } }).media.id))
    }
    const seqs = new Set(gone.map((m) => m.seq))
    messages.value = { ...messages.value, [conv]: all.filter((m) => !seqs.has(m.seq)) }
  }

  /** In-flight uploads per conversation, for progress display. */
  const uploads = ref<Record<string, { name: string; stage: string; progress: number }[]>>({})

  async function sendFile(conv: Uuid, file: File): Promise<void> {
    const entry = { name: file.name, stage: 'encrypting', progress: 0 }
    uploads.value[conv] = [...(uploads.value[conv] ?? []), entry]
    const live = () => uploads.value[conv]?.find((u) => u === entry || u.name === entry.name)
    try {
      await requireEngine().sendFile(conv, file, {
        onProgress: (stage, f) => {
          const u = live()
          if (u) Object.assign(u, { stage, progress: f })
        },
      })
    } finally {
      uploads.value[conv] = (uploads.value[conv] ?? []).filter((u) => u.name !== entry.name)
    }
  }

  async function shareLibraryItem(conv: Uuid, item: LibraryRef, note?: string): Promise<void> {
    await requireEngine().sendEnvelope(conv, {
      v: 1,
      id: newConversationId(),
      ts: Date.now(),
      type: 'library',
      item,
      ...(note ? { body: note } : {}),
    })
  }

  /** Server features and the palette (admins can change the palette at any time). */
  // Libraries go through the engine: names, keys and sharing are end-to-end encrypted.
  /** Libraries opened since the page loaded: opening one is what picks up its key and, in a
   * sealed bucket, how to sign for its files (playLibrary). */
  const openedLibraries = new Set<string>()
  const libraries = async () => {
    const ls = await requireEngine().libraries()
    for (const l of ls) openedLibraries.add(l.id)
    return ls
  }
  const createLibrary = (name: string, location: string, kind: LibraryKind = 'files') =>
    requireEngine().createLibrary(name, location, kind)
  const renameLibrary = (id: string, name: string) => requireEngine().renameLibrary(id, name)
  const shareLibrary = (id: string, space: Uuid, role?: string) => requireEngine().shareLibrary(id, space, role)
  const grantLibrary = (id: string, user: string) => requireEngine().grantLibrary(id, user)
  const libraryKey = (id: string) => engine.value?.libraryKey(id)
  /** Something a media element can play for a library reference: its entry, read and
   * decrypted on this device. (References to folders the server read itself, from before
   * HRTH-183, can't be played any more.) */
  async function playLibrary(item: LibraryRef): Promise<PlayResponse> {
    if (!item.encrypted) throw new Error('That file was in a folder on the server, which is gone: ask for it again')
    const { playEncrypted } = await import('../lib/libraryPlay')
    // Played from a room or a chat share before the Library view opened it (a page just
    // loaded): without this a sealed bucket's files can't be signed for, and never play.
    if (!openedLibraries.has(item.library)) await libraries().catch(() => undefined)
    return playEncrypted(api, libraryKey(item.library), engine.value?.token ?? '', item)
  }

  async function loadInstance(): Promise<void> {
    const e = engine.value
    if (!e) return
    try {
      const i = await e.api.instance()
      if (i.features) features.value = i.features
      serverName.value = i.name
      palette.value = i.palette ?? []
      profilePhotos.value = i.profile_photos ?? true
      applyFonts(i.fonts)
    } catch {
      // offline: keep what we knew
    }
  }

  /** Saves (or clears) my designed profile picture. */
  async function setPixels(pixels: string | null): Promise<void> {
    await requireEngine().api.setPixels(pixels)
    await refresh()
  }

  /** Saves (or clears) a space's symbol, encrypted with the space key. */
  async function setSpaceIcon(space: Uuid, pixels: string | null): Promise<void> {
    await requireEngine().setSpaceIcon(space, pixels)
  }

  // Space symbols are end-to-end encrypted, so the server can't recolour them when the
  // palette loses a colour: a member who may edit the symbol re-saves it in the new palette
  // (once per symbol per session; everyone draws it remapped meanwhile).
  const recoloured = new Set<string>()
  watch(
    () => [spaces.value, palette.value] as const,
    ([all, pal]) => {
      if (!pal.length) return
      for (const sp of all) {
        const icon = parsePixels(sp.icon)
        if (!icon || fitsPalette(icon, pal) || !can(sp, 'rename_space')) continue
        const key = `${sp.id}:${sp.icon}`
        if (recoloured.has(key)) continue
        recoloured.add(key)
        void setSpaceIcon(sp.id, formatPixels(conformIcon(icon, pal))).catch(() => undefined)
      }
    },
  )

  /** Re-reads my admin status and whether I may create spaces (both can change). */
  async function loadMe(): Promise<void> {
    const e = engine.value
    if (!e) return
    try {
      const m = await e.api.me()
      isAdmin.value = m.is_admin
      canCreateSpaces.value = m.can_create_spaces ?? true
      canLinkStorage.value = m.can_link_storage ?? m.is_admin
    } catch {
      // offline: keep what we knew
    }
  }

  async function createSpace(name: string): Promise<Uuid> {
    const e = requireEngine()
    const id = await e.createSpace(name)
    await refresh()
    void loadMe()
    return id
  }

  async function createChannel(space: Uuid, name: string): Promise<Uuid> {
    const e = requireEngine()
    const id = await e.createChannel(space, name)
    await refresh()
    return id
  }

  /** Opens a DM with one or more people: one person reuses your 1:1 with them, if any. */
  async function createDm(peers: string[]): Promise<Uuid> {
    const e = requireEngine()
    const [peer] = peers
    const existing = peers.length === 1 && dms.value.find((c) => !c.host && !c.group && c.members.length === 2 && c.members.includes(peer!))
    if (existing) return existing.id
    const id = await e.createDm(peers)
    await refresh()
    return id
  }

  async function setMuted(conv: Uuid, on: boolean): Promise<void> {
    await requireEngine().api.setMuted(conv, on)
    await refresh()
  }

  async function addDmMembers(conv: Uuid, users: string[]): Promise<void> {
    await requireEngine().addDmMembers(conv, users)
  }

  async function removeDmMember(conv: Uuid, user: string): Promise<void> {
    await requireEngine().removeDmMember(conv, user)
  }

  async function renameDm(conv: Uuid, name: string | null): Promise<void> {
    await requireEngine().renameDm(conv, name)
  }

  async function leaveDm(conv: Uuid): Promise<void> {
    await requireEngine().leaveDm(conv)
  }

  async function leaveSpace(space: Uuid): Promise<void> {
    await requireEngine().leaveSpace(space)
  }

  async function kick(space: Uuid, user: string): Promise<void> {
    await requireEngine().kick(space, user)
  }

  /** Moderation (HRTH-95): suspensions are the server's to enforce. */
  async function suspendMember(space: Uuid, user: string, minutes: number): Promise<void> {
    await requireEngine().api.suspendMember(space, user, minutes)
    await refresh()
  }

  async function liftSuspension(space: Uuid, user: string): Promise<void> {
    await requireEngine().api.liftSuspension(space, user)
    await refresh()
  }

  /** Hides (or un-hides) someone else's message for everyone; clients check the sender may. */
  async function hideMessage(conv: Uuid, target: Uuid, hidden: boolean): Promise<void> {
    await requireEngine().sendEnvelope(conv, { ...envelopeBase(), type: 'hide', target, hidden })
  }

  /** Who's in a conversation's party, plus its stored messages (for what's playing). */
  async function loadPartyParticipants(conv: Uuid): Promise<void> {
    try {
      const { participants } = await requireEngine().api.partyParticipants(conv)
      inParty.value = { ...inParty.value, [conv]: participants }
      if (participants.length && !messages.value[conv]?.length) await loadMessages(conv)
    } catch {
      // Best effort: the next `party` frame fills it in.
    }
  }

  async function loadCallParticipants(conv: Uuid): Promise<void> {
    if (!features.value.calls) return
    try {
      const { participants } = await requireEngine().api.callParticipants(conv)
      inCall.value = { ...inCall.value, [conv]: participants }
    } catch {
      // Best effort: the next `call` frame fills it in.
    }
  }

  async function renameSpace(space: Uuid, name: string, channel?: Uuid): Promise<void> {
    await requireEngine().rename(space, name, channel)
  }

  async function setChannelColor(space: Uuid, conv: Uuid, color: string | null): Promise<void> {
    await requireEngine().setChannelColor(space, conv, color)
  }

  async function deleteChannel(space: Uuid, conv: Uuid): Promise<void> {
    await requireEngine().deleteChannel(space, conv)
  }

  async function deleteSpace(space: Uuid): Promise<void> {
    await requireEngine().deleteSpace(space)
  }

  async function setRole(space: Uuid, user: string, role: Role): Promise<void> {
    await requireEngine().setRole(space, user, role)
  }

  async function updateDisplayName(name: string): Promise<void> {
    await requireEngine().api.updateMe(name)
    await refresh()
  }

  async function signOut(): Promise<void> {
    await useUnread().stopPush()
    if (catchUp) clearInterval(catchUp)
    socket?.stop()
    socket = null
    stopTransfer()
    notTransferred.clear()
    await engine.value?.signOut()
    if (isApp) await appInvoke('media_forget').catch(() => undefined)
    engine.value = null
    sync.value = null
    messages.value = {}
    preloaded = false
    status.value = 'signed-out'
  }

  // Opt-in debugging hook for QA (localStorage.hrthDebug = '1').
  try {
    if (globalThis.localStorage?.getItem('hrthDebug') === '1') {
      ;(globalThis as unknown as { __hrth: unknown }).__hrth = { engine, sendFile, messages }
    }
  } catch {
    // storage unavailable
  }

  return {
    status,
    bootError,
    engine,
    sync,
    socketState,
    messages,
    toasts,
    isAdmin,
    canCreateSpaces,
    canLinkStorage,
    loadMe,
    online,
    nodeOnline,
    inCall,
    inParty,
    loadPartyParticipants,
    loadCallParticipants,
    features,
    profilePhotos,
    palette,
    loadInstance,
    setPixels,
    setSpaceIcon,
    me,
    spaces,
    dms,
    profiles,
    conversations,
    toast,
    spaceLanding,
    storageHandle,
    exportData,
    importHistory,
    transferSource,
    boot,
    useHere,
    refresh,
    register,
    linkDevice,
    recoverAccount,
    acceptInvite,
    loadMessages,
    send,
    react,
    editMessage,
    deleteMessage,
    deleteFromDevice,
    suspendMember,
    liftSuspension,
    hideMessage,
    sendFile,
    uploads,
    shareLibraryItem,
    libraries,
    createLibrary,
    renameLibrary,
    shareLibrary,
    grantLibrary,
    libraryKey,
    playLibrary,
    createSpace,
    createChannel,
    createDm,
    leaveDm,
    addDmMembers,
    removeDmMember,
    renameDm,
    muted,
    setMuted,
    serverName,
    leaveSpace,
    kick,
    renameSpace,
    setChannelColor,
    deleteChannel,
    deleteSpace,
    setRole,
    updateDisplayName,
    signOut,
    api,
  }
})
